import {
  createContext,
  useContext,
  useMemo,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { isRightToLeft } from "@universe/i18n";
import { createTranslate, ensureLocaleTranslations, type Translate } from "./index";
import {
  DEFAULT_LOCALE,
  AVAILABLE_LOCALES,
  LOCALE_LABELS,
  isValidLocale,
  type SiteLocale,
} from "./locales";

const escapeRegex = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const LOCALE_PREFIX_PATTERN = AVAILABLE_LOCALES.map(escapeRegex).join("|");

const PREFERRED_LOCALE_COOKIE = "preferred-locale";

const readPreferredLocale = (): SiteLocale | undefined => {
  if (typeof document === "undefined") {
    return undefined;
  }
  const parts = document.cookie.split(";");
  for (const part of parts) {
    const [rawKey, ...rest] = part.split("=");
    if (!rawKey) continue;
    const key = rawKey.trim();
    if (key !== PREFERRED_LOCALE_COOKIE) continue;
    const value = rest.join("=").trim();
    if (isValidLocale(value)) {
      return value;
    }
  }
  return undefined;
};

/**
 * Build a path with the given locale prefix (default locale has no prefix).
 */
export function buildLocalePath(path: string, locale: SiteLocale): string {
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  if (locale === DEFAULT_LOCALE) {
    return cleanPath;
  }
  return cleanPath === "/" ? `/${locale}` : `/${locale}${cleanPath}`;
}

/**
 * Get the path without the locale prefix.
 */
export function getPathWithoutLocale(pathname: string, locale: SiteLocale): string {
  if (locale === DEFAULT_LOCALE) {
    return pathname;
  }
  const stripped = pathname.replace(new RegExp(`^/${locale}(/|$)`), "/");
  return stripped === "" ? "/" : stripped;
}

interface LocaleContextValue {
  /** Current locale code */
  locale: SiteLocale;
  /** Translation function instance */
  translate: Translate;
  /** Shorthand for translate.t() */
  t: Translate["t"];
  /** Available locales with their labels */
  availableLocales: Array<{ code: SiteLocale; label: string }>;
  /** Change the current locale (navigates to new URL) */
  setLocale: (locale: SiteLocale) => void;
  /** True while a locale change is loading and pending navigation */
  isLocaleSwitching: boolean;
  /** Build a path with the current locale prefix (default locale has no prefix) */
  localePath: (path: string) => string;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

interface LocaleProviderProps {
  children: ReactNode;
}

/**
 * Extract locale from pathname.
 * Returns the locale if path starts with a valid locale prefix, otherwise DEFAULT_LOCALE.
 */
function getLocaleFromPath(pathname: string): SiteLocale {
  const firstSegment = pathname.split("/")[1] || "";
  if (isValidLocale(firstSegment) && firstSegment !== DEFAULT_LOCALE) {
    return firstSegment;
  }
  return DEFAULT_LOCALE;
}

/**
 * Provides locale context to the app.
 * Automatically detects locale from the current URL path.
 * Must be used within a React Router context.
 */
export function LocaleProvider({ children }: LocaleProviderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [translationRevision, setTranslationRevision] = useState(0);
  const [isLocaleSwitching, setIsLocaleSwitching] = useState(false);
  const localeSwitchRequestIdRef = useRef(0);

  // Derive locale from URL path
  const locale = getLocaleFromPath(location.pathname);

  // Create translate instance for the current locale
  const translate = useMemo(
    () => createTranslate(locale),
    [locale, translationRevision]
  );

  // Available locales with labels
  const availableLocales = useMemo(
    () =>
      AVAILABLE_LOCALES.map((code) => ({
        code,
        label: LOCALE_LABELS[code],
      })),
    []
  );

  // Change locale by navigating to the new URL
  const setLocale = useCallback(
    (newLocale: SiteLocale) => {
      if (newLocale === locale) return;

      const pathWithoutLocale = getPathWithoutLocale(location.pathname, locale);

      // Build new path
      let newPath: string;
      if (newLocale === DEFAULT_LOCALE) {
        newPath = pathWithoutLocale;
      } else {
        newPath =
          pathWithoutLocale === "/"
            ? `/${newLocale}`
            : `/${newLocale}${pathWithoutLocale}`;
      }

      const targetUrl = `${newPath}${location.search}${location.hash}`;
      const requestId = ++localeSwitchRequestIdRef.current;

      setIsLocaleSwitching(true);

      ensureLocaleTranslations(newLocale)
        .catch(() => {
          // Continue navigation even if locale loading fails.
        })
        .finally(() => {
          if (localeSwitchRequestIdRef.current !== requestId) {
            return;
          }

          navigate(targetUrl);

          // Save preference to cookie (1 year expiry)
          try {
            const maxAge = 365 * 24 * 60 * 60;
            document.cookie = `preferred-locale=${newLocale};path=/;max-age=${maxAge};SameSite=Lax`;
          } catch {
            // Ignore cookie errors
          }

          setIsLocaleSwitching(false);
        });

    },
    [locale, location.hash, location.pathname, location.search, navigate]
  );

  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }
    const dir = isRightToLeft(locale) ? "rtl" : "ltr";
    document.documentElement.setAttribute("dir", dir);
    if (document.body) {
      document.body.setAttribute("dir", dir);
    }
  }, [locale]);

  useEffect(() => {
    let isActive = true;

    ensureLocaleTranslations(locale)
      .then(() => {
        if (isActive) {
          // Recreate translate after the load promise settles.
          // In StrictMode, mount effects run twice and the second call may await
          // an in-flight locale load; this guarantees localized strings refresh.
          setTranslationRevision((rev) => rev + 1);
        }
      })
      .catch(() => {
        // Keep current translations if loading fails.
      });

    return () => {
      isActive = false;
    };
  }, [locale]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const firstSegment = location.pathname.split("/")[1] || "";
    if (firstSegment === DEFAULT_LOCALE && location.pathname.startsWith(`/${DEFAULT_LOCALE}`)) {
      return;
    }
    if (isValidLocale(firstSegment) && firstSegment !== DEFAULT_LOCALE) {
      return;
    }
    const preferred = readPreferredLocale();
    if (!preferred || preferred === DEFAULT_LOCALE) {
      return;
    }
    if (preferred === locale) {
      return;
    }
    const nextPath = buildLocalePath(location.pathname, preferred);
    if (nextPath === location.pathname) {
      return;
    }

    let cancelled = false;
    ensureLocaleTranslations(preferred)
      .catch(() => {
        // Continue navigation even if locale loading fails.
      })
      .finally(() => {
        if (cancelled) {
          return;
        }
        navigate(`${nextPath}${location.search}${location.hash}`, { replace: true });
      });

    return () => {
      cancelled = true;
    };
  }, [locale, location.hash, location.pathname, location.search, navigate]);

  // Build a path with the appropriate locale prefix
  const localePath = useCallback(
    (path: string) => buildLocalePath(path, locale),
    [locale]
  );

  const value = useMemo(
    () => ({
      locale,
      translate,
      t: translate.t,
      availableLocales,
      setLocale,
      isLocaleSwitching,
      localePath,
    }),
    [locale, translate, availableLocales, setLocale, isLocaleSwitching, localePath]
  );

  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  );
}

/**
 * Hook to access the locale context.
 * Must be used within a LocaleProvider.
 */
export function useLocale(): LocaleContextValue {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error("useLocale must be used within a LocaleProvider");
  }
  return context;
}

interface RouteMatchOptions {
  /** If true, match the route exactly. Default: true (except for "/" which always matches exactly) */
  end?: boolean;
}

type RouteMatchFn = (route: string, options?: RouteMatchOptions) => boolean;

/**
 * Hook that returns a route matching function that handles locale-prefixed URLs.
 * Use this instead of multiple useMatch() calls to avoid hook count mismatches
 * when navigating between locale-prefixed and non-prefixed routes.
 *
 * Matches both /route and /:locale/route patterns (e.g., /about and /es/about).
 *
 * @returns Function to check if current path matches a route
 *
 * @example
 * const routeMatches = useLocaleRouteMatch();
 * const isHome = routeMatches("/");
 * const isAbout = routeMatches("/about");
 * const isShopOrSubpage = routeMatches("/shop", { end: false });
 */
export function useLocaleRouteMatch(): RouteMatchFn {
  const location = useLocation();
  const pathname = location.pathname;

  const routeMatches: RouteMatchFn = (route, options = {}) => {
    if (route === "/") {
      // Home matches "/" or "/:locale" for supported locales.
      return pathname === "/" || new RegExp(`^/(?:${LOCALE_PREFIX_PATTERN})$`).test(pathname);
    }

    const { end = true } = options;
    const escapedRoute = route.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

    // Pattern: optional locale prefix + route + optional trailing content
    const patternStr = end
      ? `^(\\/(?:${LOCALE_PREFIX_PATTERN}))?${escapedRoute}$`
      : `^(\\/(?:${LOCALE_PREFIX_PATTERN}))?${escapedRoute}(\\/.*)?$`;

    const pattern = new RegExp(patternStr);
    return pattern.test(pathname);
  };

  return routeMatches;
}
